/**
 * Floating formatting toolbar (bubble menu): appears over selected text.
 * Positioning is managed by @tiptap/extension-bubble-menu; this component
 * only provides the element and the buttons.
 */
import { createEffect, createSignal, For, onCleanup } from "solid-js";
import type { Editor } from "@tiptap/core";
import { Bold, Code, Heading1, Heading2, Italic, List, Strikethrough } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { cn } from "@apunte-digital/shared";
import { useI18n } from "@/providers/i18n-provider";

interface SelectionToolbarProps {
  editor: () => Editor | null;
  setElement: (element: HTMLDivElement) => void;
}

export function SelectionToolbar(props: SelectionToolbarProps) {
  const { t } = useI18n();
  const [version, setVersion] = createSignal(0);

  // Re-evaluate isActive() on every editor transaction
  createEffect(() => {
    const editor = props.editor();
    if (!editor) {
      return;
    }
    const bump = () => setVersion((value) => value + 1);
    editor.on("transaction", bump);
    onCleanup(() => {
      editor.off("transaction", bump);
    });
  });

  const isActive = (name: string, attrs?: Record<string, unknown>) => {
    version();
    return props.editor()?.isActive(name, attrs) ?? false;
  };

  const buttons = () => [
    {
      id: "bold",
      label: t(msg`Bold`),
      icon: Bold,
      active: isActive("bold"),
      run: () => props.editor()?.chain().focus().toggleBold().run(),
    },
    {
      id: "italic",
      label: t(msg`Italic`),
      icon: Italic,
      active: isActive("italic"),
      run: () => props.editor()?.chain().focus().toggleItalic().run(),
    },
    {
      id: "strike",
      label: t(msg`Strikethrough`),
      icon: Strikethrough,
      active: isActive("strike"),
      run: () => props.editor()?.chain().focus().toggleStrike().run(),
    },
    {
      id: "code",
      label: t(msg`Inline code`),
      icon: Code,
      active: isActive("code"),
      run: () => props.editor()?.chain().focus().toggleCode().run(),
    },
    {
      id: "h1",
      label: t(msg`Heading 1`),
      icon: Heading1,
      active: isActive("heading", { level: 1 }),
      run: () => props.editor()?.chain().focus().toggleHeading({ level: 1 }).run(),
    },
    {
      id: "h2",
      label: t(msg`Heading 2`),
      icon: Heading2,
      active: isActive("heading", { level: 2 }),
      run: () => props.editor()?.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      id: "bulletList",
      label: t(msg`Bullet list`),
      icon: List,
      active: isActive("bulletList"),
      run: () => props.editor()?.chain().focus().toggleBulletList().run(),
    },
  ];

  return (
    <div
      ref={props.setElement}
      class="selection-toolbar"
      style={{ visibility: "hidden", position: "absolute" }}
    >
      <For each={buttons()}>
        {(button) => (
          <button
            type="button"
            class={cn("selection-toolbar-button", button.active && "is-active")}
            onMouseDown={(e) => {
              // Do not steal focus from the selection
              e.preventDefault();
              button.run();
            }}
            aria-label={button.label}
            title={button.label}
          >
            <button.icon class="h-4 w-4" />
          </button>
        )}
      </For>
    </div>
  );
}
